import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	ServiceUnavailableErrorCode,
	type SmsSettingsDTO,
	type SmsSettingsInput,
	type TenantConfigPlane,
	ValidationErrorCode,
} from '@today/types'
import { SmsTestErrorKind } from '@today/ui-domain'
import { isAxiosError } from 'axios'
import { smsSettingsService } from '@/services/sms-settings.service'

/** Why a save was refused, in the terms the section shows it (TO-383 Contract errors). */
export type SmsSettingsSaveError = { kind: 'invalid'; field: string } | { kind: 'unavailable' } | { kind: 'failed' }

interface ApiErrorBody {
	error?: { code?: string; details?: { field?: string } }
}

const errorBodyOf = (error: unknown) => (isAxiosError<ApiErrorBody>(error) ? error.response?.data?.error : undefined)

/** Maps a save failure to its kind: a field rule, the secret store down (503), or anything else. */
export const saveErrorOf = (error: unknown): SmsSettingsSaveError => {
	const body = errorBodyOf(error)
	if (body?.code === ValidationErrorCode.SMS_SETTINGS_INVALID)
		return { kind: 'invalid', field: body.details?.field ?? '' }
	if (isAxiosError(error) && error.response?.status === 503) return { kind: 'unavailable' }
	return { kind: 'failed' }
}

const TEST_ERROR_KINDS: Readonly<Record<string, SmsTestErrorKind>> = {
	[ValidationErrorCode.SMS_SETTINGS_INVALID]: SmsTestErrorKind.INVALID_PHONE,
	[ValidationErrorCode.SMS_TEST_RECIPIENT_NOT_ALLOWED]: SmsTestErrorKind.RECIPIENT_NOT_ALLOWED,
	[ValidationErrorCode.SMS_COUNTRY_NOT_ALLOWED]: SmsTestErrorKind.COUNTRY_NOT_ALLOWED,
	[ValidationErrorCode.SMS_SPEND_CAP_REACHED]: SmsTestErrorKind.SPEND_CAP_REACHED,
	[ValidationErrorCode.SMS_PROVIDER_AUTH_FAILED]: SmsTestErrorKind.CREDENTIALS_REJECTED,
	[ValidationErrorCode.SMS_PROVIDER_REJECTED]: SmsTestErrorKind.PROVIDER_REJECTED,
	[ServiceUnavailableErrorCode.SMS_PROVIDER_UNAVAILABLE]: SmsTestErrorKind.PROVIDER_UNAVAILABLE,
}

/** Maps a test-SMS refusal to the kind the panel shows; anything unrecognised is a generic failure. */
export const testErrorOf = (error: unknown): SmsTestErrorKind =>
	TEST_ERROR_KINDS[errorBodyOf(error)?.code ?? ''] ?? SmsTestErrorKind.FAILED

/**
 * A tenant's SMS settings, their save and the test SMS (TO-383 REQ-014), on
 * the route tree of the console's plane. A stored save or a sent test refetches
 * the settings, so the stored state and the newest test show.
 */
export function useSmsSettings(tenantId: string, tenantSlug: string, plane: TenantConfigPlane) {
	const client = useQueryClient()
	const queryKey = ['sms-settings', plane, tenantId] as const
	const query = useQuery<SmsSettingsDTO>({
		queryKey,
		queryFn: () => smsSettingsService.get(tenantId, tenantSlug, plane),
	})
	const saveMutation = useMutation({
		mutationFn: (input: SmsSettingsInput) => smsSettingsService.save(tenantId, tenantSlug, plane, input),
		onSuccess: () => client.invalidateQueries({ queryKey }),
	})
	const testMutation = useMutation({
		mutationFn: (to: string) => smsSettingsService.test(tenantId, tenantSlug, plane, { to }),
		onSuccess: () => client.invalidateQueries({ queryKey }),
	})
	return {
		settings: query.data ?? null,
		loadFailed: query.isError,
		reload: () => void query.refetch(),
		save: (input: SmsSettingsInput) => saveMutation.mutate(input),
		saving: saveMutation.isPending,
		saveError: saveMutation.isError ? saveErrorOf(saveMutation.error) : null,
		sendTest: (to: string) => testMutation.mutate(to),
		testing: testMutation.isPending,
		testSent: testMutation.isSuccess,
		testError: testMutation.isError ? testErrorOf(testMutation.error) : null,
	}
}
